import React, { useState } from "react";
import { FiCreditCard, FiPlus, FiTrash2, FiStar } from "react-icons/fi";
import "./PaymentMethods.css";

const CARD_BRANDS = {
  4: { name: "Visa", color: "#1a1f71" },
  5: { name: "Mastercard", color: "#eb001b" },
  3: { name: "Amex", color: "#007bc1" },
};

const getCardBrand = (number) => CARD_BRANDS[parseInt(number?.[0])] || { name: "Card", color: "#64748b" };

const PaymentMethods = () => {
  const [cards, setCards] = useState([
    { id: 1, last4: "4242", expiry: "12/26", name: "Sathish Kumar", type: "4", primary: true },
    { id: 2, last4: "5555", expiry: "08/25", name: "Sathish Kumar", type: "5", primary: false },
  ]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ number: "", expiry: "", cvv: "", name: "" });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.number || form.number.replace(/\s/g, "").length < 16) e.number = "Enter a valid 16-digit card number";
    if (!form.expiry || !/^\d{2}\/\d{2}$/.test(form.expiry)) e.expiry = "Format: MM/YY";
    if (!form.cvv || form.cvv.length < 3) e.cvv = "Enter valid CVV";
    if (!form.name.trim()) e.name = "Cardholder name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(\d{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const handleAddCard = () => {
    if (!validate()) return;
    const last4 = form.number.replace(/\s/g, "").slice(-4);
    const type = form.number.replace(/\s/g, "")[0];
    setCards(prev => [...prev, { id: Date.now(), last4, expiry: form.expiry, name: form.name, type, primary: false }]);
    setForm({ number: "", expiry: "", cvv: "", name: "" });
    setShowModal(false);
  };

  const handleDelete = (id) => {
    setCards(prev => prev.filter(c => c.id !== id));
  };

  const handleSetPrimary = (id) => {
    setCards(prev => prev.map(c => ({ ...c, primary: c.id === id })));
  };

  return (
    <div className="payment-page">
      <div className="payment-header-row">
        <div>
          <h3>Saved Payment Methods</h3>
          <p>{cards.length} card{cards.length !== 1 ? "s" : ""} saved</p>
        </div>
        <button className="add-card-btn" onClick={() => setShowModal(true)}>
          <FiPlus size={16} /> Add Card
        </button>
      </div>

      {cards.length === 0 ? (
        <div className="no-cards">
          <FiCreditCard size={48} />
          <p>No payment methods saved</p>
          <button className="add-card-btn" onClick={() => setShowModal(true)}>
            Add your first card
          </button>
        </div>
      ) : (
        <div className="cards-grid">
          {cards.map(card => {
            const brand = getCardBrand(card.type);
            return (
              <div key={card.id} className={`card-chip ${card.primary ? "primary" : ""}`}>
                <div className="card-chip-top">
                  <span className="card-brand-badge" style={{ background: brand.color }}>{brand.name}</span>
                  {card.primary && <span className="primary-badge"><FiStar size={12} /> Primary</span>}
                </div>
                <div className="card-number-display">•••• •••• •••• {card.last4}</div>
                <div className="card-chip-bottom">
                  <div>
                    <span className="card-label">Cardholder</span>
                    <span className="card-value">{card.name}</span>
                  </div>
                  <div>
                    <span className="card-label">Expires</span>
                    <span className="card-value">{card.expiry}</span>
                  </div>
                  <div className="card-actions">
                    {!card.primary && (
                      <button className="card-action-btn" onClick={() => handleSetPrimary(card.id)} title="Set as primary">
                        <FiStar size={14} />
                      </button>
                    )}
                    <button className="card-action-btn danger" onClick={() => handleDelete(card.id)} title="Remove card">
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Card Modal */}
      {showModal && (
        <div className="payment-overlay" onClick={() => setShowModal(false)}>
          <div className="payment-modal" onClick={e => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h2>Add Payment Method</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="payment-modal-body">
              <div className="pm-field">
                <label>Card Number</label>
                <div className="pm-input-wrap">
                  <FiCreditCard className="pm-icon" />
                  <input
                    type="text"
                    value={form.number}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    className={errors.number ? "input-error" : ""}
                    onChange={e => setForm(prev => ({ ...prev, number: formatCardNumber(e.target.value) }))}
                  />
                </div>
                {errors.number && <span className="error-text">{errors.number}</span>}
              </div>

              <div className="pm-row">
                <div className="pm-field">
                  <label>Expiry Date</label>
                  <input
                    type="text"
                    value={form.expiry}
                    placeholder="MM/YY"
                    maxLength={5}
                    className={errors.expiry ? "input-error" : ""}
                    onChange={e => setForm(prev => ({ ...prev, expiry: formatExpiry(e.target.value) }))}
                  />
                  {errors.expiry && <span className="error-text">{errors.expiry}</span>}
                </div>
                <div className="pm-field">
                  <label>CVV</label>
                  <input
                    type="password"
                    value={form.cvv}
                    placeholder="•••"
                    maxLength={4}
                    className={errors.cvv ? "input-error" : ""}
                    onChange={e => setForm(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, "") }))}
                  />
                  {errors.cvv && <span className="error-text">{errors.cvv}</span>}
                </div>
              </div>

              <div className="pm-field">
                <label>Cardholder Name</label>
                <input
                  type="text"
                  value={form.name}
                  placeholder="Name as on card"
                  className={errors.name ? "input-error" : ""}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>

              <div className="pm-secure-note">
                🔒 Your card details are encrypted and secure
              </div>

              <div className="payment-modal-footer">
                <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-save" onClick={handleAddCard}>Add Card</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;
